import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { RoomJoinCard } from "@/components/LudoRoom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, errCode, formatXAF } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

const Pill = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`h-11 rounded-full px-5 text-sm font-semibold transition-colors ${active ? "bg-amber-500 text-[#0B0D12]" : "border border-white/10 bg-white/5 text-slate-200 hover:border-amber-500/40"}`}>
    {children}
  </button>
);

const History = () => {
  const { t } = useI18n();
  const { data } = useQuery({ queryKey: ["ludo-history"], queryFn: () => api.get("/ludo/history").then((r) => r.data) });
  if (!data?.length) return null;
  return (
    <div className="surface space-y-2 p-5" data-testid="ludo-history">
      <p className="font-display font-bold">{t("ludo.history")}</p>
      {data.slice(0, 10).map((g) => (
        <Link key={g.id} to={g.status === "cancelled" ? `/ludo/game/${g.id}` : `/ludo/verify/${g.id}`} className="flex items-center justify-between rounded-xl bg-white/[0.03] px-3 py-2 text-sm" data-testid={`ludo-history-${g.id}`}>
          <span>{g.player_count} {t("ludo.players")} · {g.mode === 2 ? t("ludo.quick") : t("ludo.classic")} · {formatXAF(g.stake)}</span>
          <span className={g.status === "cancelled" ? "text-slate-400" : g.won ? "text-emerald-400" : "text-red-300"}>
            {g.status === "cancelled" ? t("ludo.cancelled") : g.won ? `+${formatXAF(g.gain)}` : t("ludo.lost")}
          </span>
        </Link>
      ))}
    </div>
  );
};

export const LudoPanel = () => {
  const { t, tErr } = useI18n();
  const navigate = useNavigate();
  const { data: cfg } = useQuery({ queryKey: ["ludo-config"], queryFn: () => api.get("/ludo/config").then((r) => r.data), refetchInterval: 4000 });
  const [n, setN] = useState(2);
  const [mode, setMode] = useState(2);
  const [stake, setStake] = useState(250);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const roomCode = params.get("room");
  const [codeInput, setCodeInput] = useState("");

  useEffect(() => {
    api.get("/ludo/me").then(({ data }) => {
      if (data.game_id) navigate(`/ludo/game/${data.game_id}`);
      else if (data.queue) navigate("/ludo/queue");
    }).catch(() => {});
  }, [navigate]);

  if (!cfg) return null;
  const pot = stake * n;
  const commission = Math.floor((pot * cfg.ludo_commission_percent) / 100);
  const waiting = cfg.counts[`${n}:${mode}:${stake}`] || 0;
  const join = async (path) => {
    setBusy(true);
    try {
      const { data } = await api.post(path, { players: n, mode, stake });
      qc.invalidateQueries({ queryKey: ["wallet"] });
      navigate(data.status === "matched" ? `/ludo/game/${data.game_id}` : "/ludo/queue");
    } catch (e) {
      toast.error(tErr(errCode(e)));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-5" data-testid="ludo-panel">
      {roomCode && <RoomJoinCard code={roomCode.toUpperCase()} onClose={() => setParams({ game: "ludo" })} />}
      <div className="surface space-y-5 p-6">
        <div className="space-y-2"><p className="text-xs uppercase tracking-[0.2em] text-slate-500">{t("ludo.playersQ")}</p>
          <div className="flex flex-wrap gap-2">{cfg.ludo_player_counts.map((x) => <Pill key={x} active={n === x} onClick={() => setN(x)} testid={`ludo-players-${x}`}>{x}</Pill>)}</div></div>
        <div className="space-y-2"><p className="text-xs uppercase tracking-[0.2em] text-slate-500">{t("ludo.modeQ")}</p>
          <div className="flex flex-wrap gap-2">{cfg.ludo_modes.map((m) => <Pill key={m} active={mode === m} onClick={() => setMode(m)} testid={`ludo-mode-${m}`}>
            {m === 2 ? `${t("ludo.quick")} · 2 ${t("ludo.pieces")} · ${cfg.ludo_max_min_quick} min` : `${t("ludo.classic")} · 4 ${t("ludo.pieces")} · ${cfg.ludo_max_min_classic} min`}</Pill>)}</div></div>
        <div className="space-y-2"><p className="text-xs uppercase tracking-[0.2em] text-slate-500">{t("ludo.stakeQ")}</p>
          <div className="flex flex-wrap gap-2">{cfg.ludo_stakes.map((s) => <Pill key={s} active={stake === s} onClick={() => setStake(s)} testid={`ludo-stake-${s}`}>{formatXAF(s)}</Pill>)}</div></div>
        <div className="grid grid-cols-3 gap-2 text-center" data-testid="ludo-preview">
          <div className="rounded-xl bg-white/[0.04] p-3"><p className="text-[11px] text-slate-500">{t("ludo.pot")}</p><p className="font-mono font-bold" data-testid="ludo-preview-pot">{formatXAF(pot)}</p></div>
          <div className="rounded-xl bg-white/[0.04] p-3"><p className="text-[11px] text-slate-500">{t("ludo.commission")}</p><p className="font-mono font-bold" data-testid="ludo-preview-commission">{formatXAF(commission)}</p></div>
          <div className="rounded-xl bg-amber-500/10 p-3"><p className="text-[11px] text-amber-300">{t("ludo.gain")}</p><p className="font-mono font-bold text-amber-300" data-testid="ludo-preview-gain">{formatXAF(pot - commission)}</p></div>
        </div>
        <p className="text-xs text-slate-400" data-testid="ludo-waiting-count">{waiting} {t("ludo.waiting")}</p>
        <button type="button" onClick={() => join("/ludo/queue/join")} disabled={busy} data-testid="ludo-join-btn"
          className="h-14 w-full rounded-full bg-amber-500 font-display text-lg font-bold text-[#0B0D12] transition-transform active:scale-95 disabled:opacity-60">
          {t("ludo.join")}
        </button>
        <button type="button" onClick={() => join("/ludo/rooms")} disabled={busy} data-testid="ludo-create-room-btn"
          className="h-12 w-full rounded-full border border-amber-500/40 font-semibold text-amber-300 transition-colors hover:bg-amber-500/10 disabled:opacity-60">
          {t("ludo.privateRoom")}
        </button>
      </div>
      <form className="surface flex gap-2 p-4" onSubmit={(e) => { e.preventDefault(); if (codeInput.trim()) setParams({ game: "ludo", room: codeInput.trim().toUpperCase() }); }} data-testid="ludo-join-code-form">
        <input value={codeInput} onChange={(e) => setCodeInput(e.target.value)} placeholder={t("ludo.codePh")} maxLength={8} aria-label={t("ludo.joinCode")}
          className="h-11 min-w-0 flex-1 rounded-full border border-white/10 bg-white/5 px-4 font-mono uppercase" data-testid="ludo-join-code-input" />
        <button type="submit" className="h-11 rounded-full bg-white/10 px-5 text-sm font-semibold" data-testid="ludo-join-code-btn">{t("ludo.go")}</button>
      </form>
      <Link to="/ludo/essai" className="block text-center text-sm text-amber-300 underline" data-testid="lobby-ludo-practice-btn">{t("ludo.practice")}</Link>
      <History />
    </div>
  );
};
